<script setup>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

import Modal from '@/components/Modal.vue'
import Select from '@/components/Form/Select.vue'
import BotTab from '@/components/dashboards/BotTab.vue'
import LoadTab from '@/components/dashboards/LoadTab.vue'
import LossTab from '@/components/dashboards/LossTab.vue'
import AgentsTab from '@/components/dashboards/AgentsTab.vue'
import ClientsTab from '@/components/dashboards/ClientsTab.vue'
import { useAlert } from '@/composables/useToast'
import { getDashboard, dashboardCsvUrl, getClientRatings } from '@/api/dashboards'
import { getInboxes } from '@/api/inboxes'

const PERIODS = [7, 30, 90]
// Operators are not split by source: a person answers wherever the queue sends them
const TABS = [
  { key: 'BOT', ready: true, report: 'bot', bySource: true },
  { key: 'LOAD', ready: true, report: 'load', bySource: true },
  { key: 'LOSSES', ready: true, report: 'losses', bySource: true },
  { key: 'AGENTS', ready: true, report: 'agents', bySource: false },
  { key: 'CLIENTS', ready: true, report: 'clients', bySource: true },
]

const store = useStore()
const router = useRouter()
const route = useRoute()
const { t } = useI18n()

const currentUser = computed(() => store.getters['auth/getCurrentUser'])
const accountId = computed(() => currentUser.value?.accounts?.[0]?.id)

// The choice lives in the address: a reload or a shared link opens the same view
const days = ref(
  PERIODS.includes(Number(route.query.days)) ? Number(route.query.days) : 30
)
const tab = ref(
  TABS.some(item => item.key === route.query.tab) ? route.query.tab : 'BOT'
)
const inboxId = ref(String(route.query.inbox || ''))
const inboxes = ref([])
const report = ref(null)
const loading = ref(false)

const inboxOptions = computed(() => [
  { value: '', label: t('DASHBOARDS.ALL_SOURCES') },
  ...inboxes.value.map(i => ({ value: String(i.id), label: i.name })),
])
// One source — its bot's name; all sources — the shared name if there is one
const botName = computed(() => {
  const chosen = inboxes.value.filter(
    i => !inboxId.value || String(i.id) === inboxId.value
  )
  const names = [...new Set(chosen.map(i => i.bot_name).filter(Boolean))]
  return names.length === 1 ? names[0] : t('CONVERSATIONS.BOT_NAME')
})
// Today is counted hourly, so the period runs up to it
const params = computed(() => {
  const to = new Date()
  const from = new Date(to)
  from.setDate(to.getDate() - (days.value - 1))
  const iso = d => d.toISOString().slice(0, 10)
  return {
    from: iso(from),
    to: iso(to),
    ...(inboxId.value ? { inbox_id: inboxId.value } : {}),
  }
})
const currentTab = computed(() => TABS.find(item => item.key === tab.value))
const reportName = computed(
  () => TABS.find(item => item.key === tab.value).report
)
const csvUrl = computed(() =>
  dashboardCsvUrl(accountId.value, reportName.value, params.value)
)
// Share of the source's threads that went to the bot, for the first tile
const routedShare = computed(() => {
  const total = report.value?.totals?.conversations
  return total
    ? Math.round((report.value.totals.routed_to_bot * 100) / total)
    : null
})
const isEmpty = computed(() => report.value && !report.value.days.length)

// A slow answer to an earlier tab or period must not land on the current one
let request = 0
const load = async () => {
  if (!accountId.value) return
  request += 1
  const mine = request
  loading.value = true
  report.value = null
  try {
    const { data } = await getDashboard(
      accountId.value,
      reportName.value,
      params.value
    )
    if (mine === request) report.value = data
  } catch (error) {
    useAlert(t('DASHBOARDS.ERROR'))
  } finally {
    loading.value = false
  }
}

// The threads behind a count of client marks: one operator's, or everyone's
const CLIENT_FACES = ['😡', '😕', '😐', '🙂', '😍']
const ratingsAgent = ref(null)
const ratingsOpen = ref(false)
const ratings = ref([])
const loadingRatings = ref(false)
const dateFormatter = new Intl.DateTimeFormat('ru', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
})
const openRatings = async agent => {
  ratingsAgent.value = agent || null
  ratingsOpen.value = true
  ratings.value = []
  loadingRatings.value = true
  try {
    // The list answers the number it was opened from: same period, same source
    const { data } = await getClientRatings(accountId.value, {
      ...params.value,
      ...(agent ? { user_id: agent.user_id } : {}),
    })
    ratings.value = data
  } catch (error) {
    useAlert(t('DASHBOARDS.ERROR'))
    ratingsOpen.value = false
  } finally {
    loadingRatings.value = false
  }
}
// A new tab: the list stays open while the thread is read
const conversationUrl = id => `/app/?c=${id}`

// The profile arrives after the page mounts on a cold load, so the account
// id is watched rather than read once
watch(
  accountId,
  async id => {
    if (!id) return
    const { data } = await getInboxes(id)
    inboxes.value = data
    load()
  },
  { immediate: true }
)
watch([days, inboxId, tab], () => {
  router.replace({
    query: {
      ...(tab.value !== 'BOT' ? { tab: tab.value } : {}),
      ...(days.value !== 30 ? { days: days.value } : {}),
      ...(inboxId.value ? { inbox: inboxId.value } : {}),
    },
  })
  load()
})
</script>

<template>
  <div class="flex flex-col flex-1 min-h-0 overflow-y-auto bg-n-background">
    <header class="flex items-center justify-between px-6 h-14 shrink-0">
      <h1 class="text-heading-1 text-n-slate-12">
        {{ $t('DASHBOARDS.TITLE') }}
      </h1>
      <div class="flex items-center gap-3">
        <div class="flex gap-1 p-0.5 rounded-lg bg-n-alpha-1">
          <button
            v-for="p in PERIODS"
            :key="p"
            type="button"
            class="px-2.5 h-7 text-xs rounded-lg"
            :class="
              days === p
                ? 'bg-n-alpha-2 text-n-slate-12 font-medium'
                : 'text-n-slate-11 hover:bg-n-alpha-1'
            "
            @click="days = p"
          >
            {{ $t(`DASHBOARDS.PERIODS.${p}`) }}
          </button>
        </div>
        <span class="text-xs text-n-slate-11">{{
          $t('DASHBOARDS.AS_OF')
        }}</span>
        <a
          :href="csvUrl"
          class="flex items-center gap-2 px-3 h-8 text-sm rounded-lg bg-n-slate-9/10 text-n-slate-12"
          download
        >
          <span class="i-lucide-download size-4" />
          {{ $t('DASHBOARDS.CSV') }}
        </a>
      </div>
    </header>

    <div class="flex flex-col gap-6 px-6 pb-10">
      <div class="flex flex-wrap items-center gap-3">
        <div class="flex gap-1 p-0.5 rounded-lg bg-n-alpha-1">
          <button
            v-for="item in TABS"
            :key="item.key"
            type="button"
            class="flex items-center gap-1.5 px-3 h-8 text-sm rounded-lg"
            :class="
              tab === item.key
                ? 'bg-n-alpha-2 text-n-slate-12 font-medium'
                : 'text-n-slate-11'
            "
            :disabled="!item.ready"
            @click="tab = item.key"
          >
            {{ $t(`DASHBOARDS.TABS.${item.key}`, { bot: botName }) }}
            <span v-if="!item.ready" class="text-[10px] text-n-slate-10">{{
              $t('DASHBOARDS.TABS.SOON')
            }}</span>
          </button>
        </div>
        <span class="text-xs text-n-slate-11">{{
          $t('DASHBOARDS.SOURCE')
        }}</span>
        <Select
          v-model="inboxId"
          :options="inboxOptions"
          :disabled="!currentTab.bySource"
        />
      </div>

      <p
        v-if="isEmpty"
        class="py-10 text-center text-body-main text-n-slate-11"
      >
        {{ $t('DASHBOARDS.EMPTY') }}
      </p>
      <BotTab
        v-else-if="report && tab === 'BOT'"
        :report="report"
        :routed-share="routedShare"
      />
      <LoadTab v-else-if="report && tab === 'LOAD'" :report="report" />
      <LossTab v-else-if="report && tab === 'LOSSES'" :report="report" />
      <AgentsTab
        v-else-if="report && tab === 'AGENTS'"
        :report="report"
        @open-ratings="openRatings"
      />
      <ClientsTab
        v-else-if="report && tab === 'CLIENTS'"
        :report="report"
        @open-ratings="openRatings()"
      />
    </div>

    <Modal
      :show="ratingsOpen"
      :title="
        ratingsAgent
          ? $t('DASHBOARDS.RATINGS.TITLE_AGENT', { name: ratingsAgent.name })
          : $t('DASHBOARDS.RATINGS.TITLE')
      "
      :description="$t('DASHBOARDS.RATINGS.DESC')"
      @close="ratingsOpen = false"
    >
      <p v-if="loadingRatings" class="py-4 text-body-main text-n-slate-11">
        {{ $t('DASHBOARDS.RATINGS.LOADING') }}
      </p>
      <p
        v-else-if="!ratings.length"
        class="py-4 text-body-main text-n-slate-11"
      >
        {{ $t('DASHBOARDS.RATINGS.EMPTY') }}
      </p>
      <div v-else class="flex flex-col overflow-y-auto max-h-96">
        <a
          v-for="row in ratings"
          :key="`${row.id}-${row.answered_at}`"
          :href="conversationUrl(row.id)"
          target="_blank"
          rel="noopener"
          class="flex flex-col w-full gap-1 px-2 py-2.5 text-left no-underline border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1"
        >
          <span class="flex items-center w-full gap-3">
            <span class="flex items-center gap-1 shrink-0">
              <span class="text-lg leading-none">{{
                CLIENT_FACES[row.rating - 1]
              }}</span>
              <span class="text-heading-3 tabular-nums text-n-slate-12">{{
                row.rating
              }}</span>
            </span>
            <span class="text-heading-3 text-n-slate-12">
              №{{ row.display_id }}
            </span>
            <span class="flex-1 truncate text-body-main text-n-slate-11">
              {{ row.contact_name || $t('CONVERSATIONS.UNKNOWN') }}
              · {{ row.inbox_name }} ·
              {{
                row.about === 'bot'
                  ? $t('DASHBOARDS.RATINGS.ABOUT_BOT')
                  : row.operator_name
              }}
            </span>
            <span class="shrink-0 text-xs text-n-slate-10">
              {{ dateFormatter.format(new Date(row.answered_at)) }}
            </span>
          </span>
          <span
            v-if="row.reasons.length || row.comment || row.called_operator"
            class="pl-8 text-xs text-n-slate-11"
          >
            <template v-if="row.reasons.length">{{
              row.reasons.join(', ')
            }}</template>
            <template v-if="row.comment">
              <template v-if="row.reasons.length"> · </template>«{{
                row.comment
              }}»</template
            >
            <span v-if="row.called_operator" class="text-n-amber-11">
              <template v-if="row.reasons.length || row.comment"> · </template
              >{{ $t('DASHBOARDS.RATINGS.CALLED') }}
            </span>
          </span>
        </a>
      </div>
    </Modal>
  </div>
</template>
